import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { CategoryService } from 'src/app/services/category';
import { LocaleService } from 'src/app/services/locale';
import { DropdownComponent } from '../../../../shared/dropdown';
import { TrashComponent } from '../../../../icons/trash';
import { PlusComponent } from '../../../../icons/plus';

// Define interfaces for our form data
interface Question0Row {
  employeeGroup: FormControl<string | null>;
  employees: FormControl<string | null>;
  transport: FormControl<string | null>;
  distance: FormControl<string | null>;
  trips: FormControl<string | null>;
  workingHours: FormControl<string | null>;
  question: FormControl<0>;
}

interface Question1Row {
  employeeGroup: FormControl<string | null>;
  employees: FormControl<string | null>;
  transport: FormControl<string | null>;
  pointA: FormControl<string | null>;
  pointB: FormControl<string | null>;
  trips: FormControl<string | null>;
  workingHours: FormControl<string | null>;
  question: FormControl<1>;
}

interface Question2Row {
  employeeGroup: FormControl<string | null>;
  employees: FormControl<string | null>;
  transport: FormControl<string | null>;
  distance: FormControl<string | null>;
  trips: FormControl<string | null>;
  workingHours: FormControl<string | null>;
  smartWorking: FormControl<string | null>;
  question: FormControl<2>;
}

type FormRow =
  | { row: Question0Row }
  | { row: Question1Row }
  | { row: Question2Row };

@Component({
  selector: 'app-category3-question3',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    DropdownComponent,
    TrashComponent,
    PlusComponent
  ],
  template: `
    <div *ngFor="let question of questionTitles; let i = index">
      <!-- Smart Working & Commuting Section -->
      <div *ngIf="i === 2">
        <div class="flex justify-between mb-4 items-center">
          <div class="text-[13px]">{{ question }}</div>
          <div (click)="createForm(2)">
            <button class="bg-[#F8F9FA] px-3 py-2 rounded text-white text-lg cursor-pointer">
              <app-plus></app-plus>
            </button>
          </div>
        </div>

        <div class="space-y-4">
          <div
            *ngFor="let control of q2Controls; let x = index"
            class="border rounded-lg p-4"
          >
            <div (click)="removeRow(2, x)" class="flex justify-end mb-4 cursor-pointer">
              <app-trash></app-trash>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_1
                }}</label>
                <app-dropdown
                  placeholder="{{ strings[language].select }} {{
                    strings[language].label_1
                  }}"
                  [options]="employeeGroupRemoteAndCommuting"
                  [control]="control.row['employeeGroup']"
                  (change)="onChange()"
                ></app-dropdown>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_2
                }}</label>
                <app-input
                  [index]="x"
                  type="text"
                  [dollar]="false"
                  placeholder="0"
                  (change)="onChange()"
                  [control]="control.row['employees']"
                ></app-input>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_3
                }}</label>
                <app-dropdown
                  placeholder="{{ strings[language].select }} {{
                    strings[language].label_3
                  }}"
                  [options]="transportModesCommuting"
                  [control]="control.row['transport']"
                  (change)="onChange()"
                ></app-dropdown>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_4
                }}</label>
                <app-input
                  type="number"
                  [dollar]="false"
                  placeholder="0"
                  (change)="onChange()"
                  [control]="control.row['distance']"
                ></app-input>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_5
                }}</label>
                <app-input
                  type="number"
                  [dollar]="false"
                  placeholder="0"
                  (change)="onChange()"
                  [control]="control.row['trips']"
                ></app-input>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_6
                }}</label>
                <app-input
                  type="number"
                  [dollar]="false"
                  placeholder="0"
                  (change)="onChange()"
                  [control]="control.row['workingHours']"
                ></app-input>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_7
                }}</label>
                <app-input
                  type="number"
                  [dollar]="false"
                  unit="%"
                  placeholder="0"
                  (change)="onChange()"
                  [control]="control.row['smartWorking']"
                ></app-input>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Urban & Public Transport Section -->
      <div *ngIf="i === 0">
        <div class="flex justify-between mb-4 items-center">
          <div class="text-[13px]">{{ question }}</div>
          <div (click)="createForm(0)">
            <button class="bg-[#F8F9FA] px-3 py-2 rounded text-white text-lg cursor-pointer">
              <app-plus></app-plus>
            </button>
          </div>
        </div>

        <div class="space-y-4">
          <div
            *ngFor="let control of q0Controls; let x = index"
            class="border rounded-lg p-4"
          >
            <div class="flex justify-end mb-4 cursor-pointer">
              <div
                (click)="removeRow(0, x)"
                class="flex justify-start align-middle items-center cursor-pointer"
              >
                <app-trash></app-trash>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_1
                }}</label>
                <app-dropdown
                  placeholder="{{ strings[language].select }} {{
                    strings[language].label_1
                  }}"
                  [options]="employeeGroupOfficeOnly"
                  [control]="control.row['employeeGroup']"
                  (change)="onChange()"
                ></app-dropdown>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_2
                }}</label>
                <app-input
                  [index]="x"
                  type="text"
                  [dollar]="false"
                  placeholder="0"
                  (change)="onChange()"
                  [control]="control.row['employees']"
                ></app-input>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_3
                }}</label>
                <app-dropdown
                  placeholder="{{ strings[language].select }} {{
                    strings[language].label_3
                  }}"
                  [options]="transportModesPublic"
                  [control]="control.row['transport']"
                  (change)="onChange()"
                ></app-dropdown>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_4
                }}</label>
                <app-input
                  type="number"
                  [dollar]="false"
                  placeholder="0"
                  (change)="onChange()"
                  [control]="control.row['distance']"
                ></app-input>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_5
                }}</label>
                <app-input
                  type="number"
                  [dollar]="false"
                  placeholder="0"
                  (change)="onChange()"
                  [control]="control.row['trips']"
                ></app-input>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_6
                }}</label>
                <app-input
                  type="number"
                  [dollar]="false"
                  placeholder="0"
                  (change)="onChange()"
                  [control]="control.row['workingHours']"
                ></app-input>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Intercity & Personal Transport Section -->
      <div *ngIf="i === 1">
        <div class="flex justify-between mb-4 items-center">
          <div class="text-[13px]">{{ question }}</div>
          <div (click)="createForm(1)">
            <button class="bg-[#F8F9FA] px-3 py-2 rounded text-white text-lg cursor-pointer">
              <app-plus></app-plus>
            </button>
          </div>
        </div>

        <div class="space-y-4">
          <div
            *ngFor="let control of q1Controls; let x = index"
            class="border rounded-lg p-4"
          >
            <div class="flex justify-end mb-4 cursor-pointer">
              <div
                (click)="removeRow(1, x)"
                class="flex justify-start align-middle items-center cursor-pointer"
              >
                <app-trash></app-trash>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_1
                }}</label>
                <app-dropdown
                  placeholder="{{ strings[language].select }} {{
                    strings[language].label_1
                  }}"
                  [options]="employeeGroupOfficeAndHybrid"
                  [control]="control.row['employeeGroup']"
                  (change)="onChange()"
                ></app-dropdown>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_2
                }}</label>
                <app-input
                  [index]="x"
                  type="text"
                  [dollar]="false"
                  placeholder="0"
                  (change)="onChange()"
                  [control]="control.row['employees']"
                ></app-input>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_3
                }}</label>
                <app-dropdown
                  placeholder="{{ strings[language].select }} {{
                    strings[language].label_3
                  }}"
                  [options]="transportModesBusinessTravel"
                  [control]="control.row['transport']"
                  (change)="onChange()"
                ></app-dropdown>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_8
                }}</label>
                <app-input
                  type="text"
                  [dollar]="false"
                  placeholder="{{ strings[language].label_8 }}"
                  (change)="onChange()"
                  [control]="control.row['pointA']"
                ></app-input>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_9
                }}</label>
                <app-input
                  type="text"
                  [dollar]="false"
                  placeholder="{{ strings[language].label_9 }}"
                  (change)="onChange()"
                  [control]="control.row['pointB']"
                ></app-input>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_5
                }}</label>
                <app-input
                  type="number"
                  [dollar]="false"
                  placeholder="0"
                  (change)="onChange()"
                  [control]="control.row['trips']"
                ></app-input>
              </div>

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_6
                }}</label>
                <app-input
                  type="number"
                  [dollar]="false"
                  placeholder="0"
                  (change)="onChange()"
                  [control]="control.row['workingHours']"
                ></app-input>
              </div>
            </div>
          </div>
        </div>
      </div>
      <br />
    </div>
  `,
  standalone: true
})
export class Category3Question3Component {
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  categories = this.categoryService.categories();

  q0Controls: { row: Question0Row }[] = [
    {
      row: {
        employeeGroup: new FormControl('', Validators.required),
        employees: new FormControl('', Validators.required),
        transport: new FormControl('', Validators.required),
        distance: new FormControl('', Validators.required),
        trips: new FormControl('', Validators.required),
        workingHours: new FormControl('', Validators.required),
        question: new FormControl(0) as FormControl<0>
      }
    }
  ];

  q1Controls: { row: Question1Row }[] = [
    {
      row: {
        employeeGroup: new FormControl('', Validators.required),
        employees: new FormControl('', Validators.required),
        transport: new FormControl('', Validators.required),
        pointA: new FormControl('', Validators.required),
        pointB: new FormControl('', Validators.required),
        trips: new FormControl('', Validators.required),
        workingHours: new FormControl('', Validators.required),
        question: new FormControl(1) as FormControl<1>
      }
    }
  ];

  q2Controls: { row: Question2Row }[] = [
    {
      row: {
        employeeGroup: new FormControl('', Validators.required),
        employees: new FormControl('', Validators.required),
        transport: new FormControl('', Validators.required),
        distance: new FormControl('', Validators.required),
        trips: new FormControl('', Validators.required),
        workingHours: new FormControl('', Validators.required),
        smartWorking: new FormControl('', Validators.required),
        question: new FormControl(2) as FormControl<2>
      }
    }
  ];

  strings: any = {
    english: {
      question: 'Urban & Public Transport',
      question2: 'Intercity & Personal Transport',
      question3: 'Smart Working & Commuting Information',
      answer: 'Please fill in the required data if yes to proceed.',
      label_1: 'Employee Group',
      label_2: 'Number of Employees',
      label_3: 'Transport Mode',
      label_4: 'Distance (km per trip)',
      label_5: 'Trips per Week',
      label_6: 'Working Hours per Week',
      label_7: 'Smart Working %',
      label_8: 'Departure',
      label_9: 'Destination',
      select: 'Select'
    },
    italian: {
      question: 'Trasporto Urbano e Pubblico',
      question2: 'Trasporto Interurbano e Personale',
      question3: 'Informazioni su Smart Working e Pendolarismo',
      answer: 'Compila i dati richiesti per procedere, se hai risposto sì.',
      label_1: 'Gruppo di Dipendenti',
      label_2: 'Numero di Dipendenti',
      label_3: 'Modalità di Trasporto',
      label_4: 'Distanza (km per viaggio)',
      label_5: 'Viaggi a Settimana',
      label_6: 'Ore Lavorative a Settimana',
      label_7: '% di Smart Working',
      label_8: 'Partenza',
      label_9: 'Destinazione',
      select: 'Seleziona'
    }
  };

  questionTitles = [
    this.strings[this.language].question, // Urban & Public Transport
    this.strings[this.language].question2, // Intercity & Personal Transport
    this.strings[this.language].question3 // Smart Working & Commuting
  ];

  transportModesCommuting: any;
  transportModesPublic: any;
  transportModesBusinessTravel: any;
  employeeGroupOfficeOnly: any;
  employeeGroupOfficeAndHybrid: any;
  employeeGroupRemoteAndCommuting: any;

  ngOnInit() {
    this.changeNameOnLnaguage();
    this.loadSavedData();
  }
  changeNameOnLnaguage() {
    const isEnglish = this.language === 'english';

    // --- Transport Modes ---
    this.transportModesCommuting = [
      { value: 'car', label: isEnglish ? 'Car' : 'Auto' },
      { value: 'bus', label: isEnglish ? 'Bus' : 'Autobus' },
      { value: 'subway', label: isEnglish ? 'Subway' : 'Metropolitana' }
    ];

    this.transportModesPublic = [
      {
        value: 'electric_bus',
        label: isEnglish ? 'Electric Bus' : 'Autobus elettrico'
      },
      { value: 'bus', label: isEnglish ? 'Bus' : 'Autobus' },
      { value: 'tram', label: isEnglish ? 'Tram' : 'Tram' },
      { value: 'subway', label: isEnglish ? 'Subway' : 'Metropolitana' }
    ];

    this.transportModesBusinessTravel = [
      { value: 'car', label: isEnglish ? 'Car' : 'Auto' },
      { value: 'train', label: isEnglish ? 'Train' : 'Treno' }
    ];

    // --- Employee Group Options ---
    this.employeeGroupOfficeOnly = [
      {
        value: 'office-workers',
        label: isEnglish ? 'Office workers' : 'Lavoratori d’ufficio'
      },
      {
        value: 'factory-workers',
        label: isEnglish
          ? 'Factory workers / Workshop staff'
          : 'Operai / Lavoratori di officina'
      },
      {
        value: 'field-technicians',
        label: isEnglish
          ? 'Field technicians / Maintenance staff'
          : 'Tecnici sul campo / Manutentori'
      },
      {
        value: 'part-time-shift-workers',
        label: isEnglish
          ? 'Part-time / Shift workers'
          : 'Lavoratori part-time / turnisti'
      },
      {
        value: 'other-staff',
        label: isEnglish
          ? 'Other staff (administrative, seasonal, etc.)'
          : 'Altro personale (amministrativi, stagionali, ecc.)'
      }
    ];

    this.employeeGroupOfficeAndHybrid = [
      {
        value: 'office-workers',
        label: isEnglish ? 'Office Workers' : 'Lavoratori d’ufficio'
      },
      {
        value: 'hybrid-workers',
        label: isEnglish ? 'Hybrid Workers' : 'Lavoratori ibridi'
      }
    ];

    this.employeeGroupRemoteAndCommuting = [
      {
        value: 'remote',
        label: isEnglish ? 'Remote' : 'Lavoro da remoto'
      },
      {
        value: 'commuting',
        label: isEnglish ? 'Commuting' : 'Pendolarismo'
      }
    ];
  }

  getLabel(value: string, options: any[]): string {
    const match = options.find(o => o.value === value);
    return match ? match.label : value;
  }

  loadSavedData() {
    const savedData = this.getSavedData();
    if (savedData) {
      this.q0Controls = savedData
        .filter((item: any) => item.question === 0)
        .map((item: any) => ({
          row: {
            employeeGroup: new FormControl(
              item.employeeGroup,
              Validators.required
            ),
            employees: new FormControl(item.employees, Validators.required),
            transport: new FormControl(item.transport, Validators.required),
            distance: new FormControl(item.distance, Validators.required),
            trips: new FormControl(item.trips, Validators.required),
            workingHours: new FormControl(
              item.workingHours,
              Validators.required
            ),
            question: new FormControl(0) as FormControl<0>
          }
        }));

      this.q1Controls = savedData
        .filter((item: any) => item.question === 1)
        .map((item: any) => ({
          row: {
            employeeGroup: new FormControl(
              item.employeeGroup,
              Validators.required
            ),
            employees: new FormControl(item.employees, Validators.required),
            transport: new FormControl(item.transport, Validators.required),
            pointA: new FormControl(item.pointA, Validators.required),
            pointB: new FormControl(item.pointB, Validators.required),
            trips: new FormControl(item.trips, Validators.required),
            workingHours: new FormControl(
              item.workingHours,
              Validators.required
            ),
            question: new FormControl(1) as FormControl<1>
          }
        }));

      this.q2Controls = savedData
        .filter((item: any) => item.question === 2)
        .map((item: any) => ({
          row: {
            employeeGroup: new FormControl(
              item.employeeGroup,
              Validators.required
            ),
            employees: new FormControl(item.employees, Validators.required),
            transport: new FormControl(item.transport, Validators.required),
            distance: new FormControl(item.distance, Validators.required),
            trips: new FormControl(item.trips, Validators.required),
            workingHours: new FormControl(
              item.workingHours,
              Validators.required
            ),
            smartWorking: new FormControl(
              item.smartWorking,
              Validators.required
            ),
            question: new FormControl(2) as FormControl<2>
          }
        }));
    }
  }

  getSavedData(): any {
    const categories = localStorage.getItem('categories');
    if (categories) {
      const parsed = JSON.parse(categories);
      return parsed?.[2]?.questions?.[2]?.answers || null;
    }
    return null;
  }

  createForm(index: 0 | 1 | 2) {
    if (index === 0) {
      this.q0Controls.push({
        row: {
          employeeGroup: new FormControl('', Validators.required),
          employees: new FormControl('', Validators.required),
          transport: new FormControl('', Validators.required),
          distance: new FormControl('', Validators.required),
          trips: new FormControl('', Validators.required),
          workingHours: new FormControl('', Validators.required),
          question: new FormControl(0) as FormControl<0>
        }
      });
    } else if (index === 1) {
      this.q1Controls.push({
        row: {
          employeeGroup: new FormControl('', Validators.required),
          employees: new FormControl('', Validators.required),
          transport: new FormControl('', Validators.required),
          pointA: new FormControl('', Validators.required),
          pointB: new FormControl('', Validators.required),
          trips: new FormControl('', Validators.required),
          workingHours: new FormControl('', Validators.required),
          question: new FormControl(1) as FormControl<1>
        }
      });
    } else {
      this.q2Controls.push({
        row: {
          employeeGroup: new FormControl('', Validators.required),
          employees: new FormControl('', Validators.required),
          transport: new FormControl('', Validators.required),
          distance: new FormControl('', Validators.required),
          trips: new FormControl('', Validators.required),
          workingHours: new FormControl('', Validators.required),
          smartWorking: new FormControl('', Validators.required),
          question: new FormControl(2) as FormControl<2>
        }
      });
    }
    this.onChange();
  }

  removeRow(questionIndex: 0 | 1 | 2, rowIndex: number) {
    if (questionIndex === 0) {
      this.q0Controls.splice(rowIndex, 1);
    } else if (questionIndex === 1) {
      this.q1Controls.splice(rowIndex, 1);
    } else {
      this.q2Controls.splice(rowIndex, 1);
    }
    this.onChange();
  }

  onChange = () => {
    if (this.categories && this.categories.length > 2) {
      const allRows = [
        ...this.q0Controls.map(control => ({
          tableName: this.strings[this.language].question,
          employeeGroup: control.row.employeeGroup.value ?? '',
          employeeGroupLabel: this.getLabel(
            control.row.employeeGroup.value ?? '',
            this.employeeGroupOfficeOnly
          ),
          employees: control.row.employees.value ?? '',
          transport: control.row.transport.value ?? '',
          transportLabel: this.getLabel(
            control.row.transport.value ?? '',
            this.transportModesPublic
          ),
          distance: control.row.distance.value ?? '',
          trips: control.row.trips.value ?? '',
          workingHours: control.row.workingHours.value ?? '',
          question: control.row.question.value ?? 0
        })),
        ...this.q1Controls.map(control => ({
          tableName: this.strings[this.language].question2,
          employeeGroup: control.row.employeeGroup.value ?? '',
          employeeGroupLabel: this.getLabel(
            control.row.employeeGroup.value ?? '',
            this.employeeGroupOfficeAndHybrid
          ),
          employees: control.row.employees.value ?? '',
          transport: control.row.transport.value ?? '',
          transportLabel: this.getLabel(
            control.row.transport.value ?? '',
            this.transportModesBusinessTravel
          ),
          pointA: control.row.pointA.value ?? '',
          pointB: control.row.pointB.value ?? '',
          trips: control.row.trips.value ?? '',
          workingHours: control.row.workingHours.value ?? '',
          question: control.row.question.value ?? 1
        })),
        ...this.q2Controls.map(control => ({
          tableName: this.strings[this.language].question3,
          employeeGroup: control.row.employeeGroup.value ?? '',
          employeeGroupLabel: this.getLabel(
            control.row.employeeGroup.value ?? '',
            this.employeeGroupRemoteAndCommuting
          ),
          employees: control.row.employees.value ?? '',
          transport: control.row.transport.value ?? '',
          transportLabel: this.getLabel(
            control.row.transport.value ?? '',
            this.transportModesCommuting
          ),
          distance: control.row.distance.value ?? '',
          trips: control.row.trips.value ?? '',
          workingHours: control.row.workingHours.value ?? '',
          smartWorking: control.row.smartWorking?.value ?? '',
          question: control.row.question.value ?? 2
        }))
      ];

      this.categories[2].questions[2].answers = allRows as any;
      this.categoryService.persistCategories(this.categories);
    }
  };
}
